import { computed, ref } from 'vue';

import useSearchParamsState from '~/hooks/useSearchParamsState';

import type { GetBlocklistRequest, SortDirection } from '../services/capitalMarketsService';

export type UseBlocklistParams = {
  ccb?: string;
  page?: number;
  page_size?: number;
  sort_direction?: SortDirection;
};

const INITIAL_FILTER_STATE: UseBlocklistParams = {
  ccb: undefined,
  page: 1,
  page_size: 20,
  sort_direction: 'desc',
};

export default function useBlocklistFilters(options?: {
  onFiltersChange?: () => void;
}) {
  const searchParams = useSearchParamsState<UseBlocklistParams>(
    INITIAL_FILTER_STATE,
    { parseArrays: false },
  );

  const localCcbSearch = ref<string>(searchParams.value.ccb || '');
  const localSortDirection = ref<SortDirection>(searchParams.value.sort_direction || 'desc');

  const apiParams = computed<GetBlocklistRequest>(() => {
    const params = searchParams.value;
    const mappedParams: GetBlocklistRequest = {};

    if (params.page) {
      mappedParams.page = Number(params.page);
    }
    if (params.page_size) {
      mappedParams.page_size = Number(params.page_size);
    }
    if (params.ccb?.trim()) {
      mappedParams.ccb = params.ccb.trim();
    }
    if (params.sort_direction) {
      mappedParams.sort_direction = params.sort_direction;
    }

    return mappedParams;
  });

  function handleSearch(): void {
    searchParams.value = {
      ...searchParams.value,
      ccb: localCcbSearch.value?.trim() || undefined,
      page: 1,
      sort_direction: localSortDirection.value,
    };

    if (options?.onFiltersChange) {
      options.onFiltersChange();
    }
  }

  function handleReset(): void {
    searchParams.value = { ...INITIAL_FILTER_STATE };
    localCcbSearch.value = '';
    localSortDirection.value = 'desc';

    if (options?.onFiltersChange) {
      options.onFiltersChange();
    }
  }

  const updateSearchParams = (updates: Partial<UseBlocklistParams>): void => {
    searchParams.value = { ...searchParams.value, ...updates };
  };

  function setPage(value: number): void {
    updateSearchParams({ page: Number(value) });
  }

  function setPageSize(value: number): void {
    updateSearchParams({
      page: 1,
      page_size: Number(value),
    });
  }

  function setSortDirection(value: SortDirection): void {
    updateSearchParams({
      page: 1,
      sort_direction: value,
    });
    localSortDirection.value = value;
  }

  function initializeForm(): void {
    localCcbSearch.value = searchParams.value.ccb || '';
    localSortDirection.value = searchParams.value.sort_direction || 'desc';
  }

  return {
    apiParams,
    handleReset,
    handleSearch,
    initializeForm,
    localCcbSearch,
    localSortDirection,
    searchParams,
    setPage,
    setPageSize,
    setSortDirection,
  };
}
